import { DayProg, FullProg_v4 } from '~/providers/types';
import {
  DamEcoSimplifiedProgrammingTitles, DamEcoSimplifiedScheduleTitle, DamWeekDayKey,
} from '~/providers/types/api-private';

/** Ordem dos dias no backend (identidade do card) e na tela (DOM → SAB, como no design). */
export const WEEK_DAY_ORDER: DamWeekDayKey[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
export const DISPLAY_DAY_ORDER: DamWeekDayKey[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/** Limite de caracteres do título livre, o mesmo do backend. */
export const PROGRAMMING_TITLE_MAX_LENGTH = 50;

export type SchedulePermission = 'allow' | 'forbid';

/** Card de programação: os dias que têm o mesmo período e funcionamento. */
export interface ScheduleGroup {
  days: DamWeekDayKey[];
  permission: SchedulePermission;
  start: string;
  end: string;
}

export interface ExceptionItem {
  /** Chave da exceção em `FullProg_v4.exceptions` (AAAA-MM-DD). */
  date: string;
  permission: SchedulePermission;
  start: string;
  end: string;
}

export interface ProgrammingState {
  programming: FullProg_v4;
  titles: DamEcoSimplifiedProgrammingTitles;
}

export type ProgrammingChangeResult = { ok: true; state: ProgrammingState } | { ok: false; reason: 'conflict' };

export const EMPTY_PROGRAMMING_TITLES: DamEcoSimplifiedProgrammingTitles = { schedules: [], exceptions: {} };

function sortDays(days: DamWeekDayKey[]): DamWeekDayKey[] {
  return WEEK_DAY_ORDER.filter((day) => days.includes(day));
}

function isSchedulePermission(permission: string): permission is SchedulePermission {
  return permission === 'allow' || permission === 'forbid';
}

/** Mesma identidade do backend: funcionamento, período e dias de segunda a domingo. */
export function scheduleGroupKey(group: Pick<DamEcoSimplifiedScheduleTitle, 'days' | 'permission' | 'start' | 'end'>): string {
  return `${group.permission}|${group.start}|${group.end}|${sortDays(group.days).join(',')}`;
}

/** Agrupa a semana em cards, como a tela do modo atual: dias com o mesmo período e funcionamento. */
export function groupWeekSchedules(week: FullProg_v4['week']): ScheduleGroup[] {
  const groups = new Map<string, ScheduleGroup>();
  for (const day of WEEK_DAY_ORDER) {
    const dayProg = week[day];
    if (!dayProg || !isSchedulePermission(dayProg.permission)) continue;
    const key = `${dayProg.permission}|${dayProg.start}|${dayProg.end}`;
    const group = groups.get(key);
    if (group) group.days.push(day);
    else { groups.set(key, {
      days: [day], permission: dayProg.permission, start: dayProg.start, end: dayProg.end,
    }); }
  }
  return [...groups.values()].sort((a, b) => a.start.localeCompare(b.start) || WEEK_DAY_ORDER.indexOf(a.days[0]) - WEEK_DAY_ORDER.indexOf(b.days[0]));
}

export function listExceptions(exceptions: FullProg_v4['exceptions']): ExceptionItem[] {
  return Object.entries(exceptions ?? {})
    .filter(([, dayProg]) => !!dayProg && !dayProg.clearProg && isSchedulePermission(dayProg.permission))
    .map(([date, dayProg]) => ({
      date, permission: dayProg.permission, start: dayProg.start, end: dayProg.end,
    }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

export function findScheduleTitle(titles: DamEcoSimplifiedProgrammingTitles, group: ScheduleGroup): string | null {
  const key = scheduleGroupKey(group);
  return titles.schedules.find((item) => scheduleGroupKey(item) === key)?.title ?? null;
}

function withoutScheduleTitle(titles: DamEcoSimplifiedProgrammingTitles, group: ScheduleGroup | null): DamEcoSimplifiedScheduleTitle[] {
  if (!group) return titles.schedules;
  const key = scheduleGroupKey(group);
  return titles.schedules.filter((item) => scheduleGroupKey(item) !== key);
}

/**
 * Adiciona ou edita um card de programação. Recusa quando algum dia escolhido já está em outro card,
 * e leva o título junto, já que ele é identificado pelo grupo de dias e período.
 */
export function saveScheduleGroup(state: ProgrammingState, previous: ScheduleGroup | null, next: ScheduleGroup & { title: string }): ProgrammingChangeResult {
  const week = { ...state.programming.week };
  for (const day of previous?.days ?? []) delete week[day];
  if (next.days.some((day) => !!week[day])) return { ok: false, reason: 'conflict' };

  const dayProg: DayProg = { permission: next.permission, start: next.start, end: next.end };
  for (const day of next.days) week[day] = dayProg;

  const schedules = withoutScheduleTitle(state.titles, previous);
  const title = next.title.trim();
  const group: ScheduleGroup = {
    days: sortDays(next.days), permission: next.permission, start: next.start, end: next.end,
  };
  return {
    ok: true,
    state: {
      programming: { ...state.programming, week },
      titles: { ...state.titles, schedules: title ? [...schedules, { ...group, title }] : schedules },
    },
  };
}

export function removeScheduleGroup(state: ProgrammingState, group: ScheduleGroup): ProgrammingState {
  const week = { ...state.programming.week };
  for (const day of group.days) delete week[day];
  return {
    programming: { ...state.programming, week },
    titles: { ...state.titles, schedules: withoutScheduleTitle(state.titles, group) },
  };
}

/** Adiciona ou edita uma exceção; recusa data que já tem outra exceção. O título acompanha a data. */
export function saveException(state: ProgrammingState, previousDate: string | null, next: ExceptionItem): ProgrammingChangeResult {
  const exceptions = { ...state.programming.exceptions };
  if (previousDate) delete exceptions[previousDate];
  if (exceptions[next.date]) return { ok: false, reason: 'conflict' };
  exceptions[next.date] = { permission: next.permission, start: next.start, end: next.end };

  const titleExceptions = { ...state.titles.exceptions };
  const previousTitle = previousDate ? titleExceptions[previousDate] : undefined;
  if (previousDate) delete titleExceptions[previousDate];
  if (previousTitle) titleExceptions[next.date] = previousTitle;

  return {
    ok: true,
    state: {
      programming: { ...state.programming, exceptions },
      titles: { ...state.titles, exceptions: titleExceptions },
    },
  };
}

export function removeException(state: ProgrammingState, date: string): ProgrammingState {
  const exceptions = { ...state.programming.exceptions };
  delete exceptions[date];
  const titleExceptions = { ...state.titles.exceptions };
  delete titleExceptions[date];
  return {
    programming: { ...state.programming, exceptions },
    titles: { ...state.titles, exceptions: titleExceptions },
  };
}

/** AAAA-MM-DD → DD/MM/AAAA. */
export function formatExceptionDate(date: string): string {
  const [year, month, day] = date.split('-');
  return day && month && year ? `${day}/${month}/${year}` : date;
}

/** Período do dia inteiro, como a tela do modo atual grava. */
export const FULL_DAY = { start: '00:00', end: '23:59' };

export function isFullDay(start: string, end: string): boolean {
  return start === FULL_DAY.start && end === FULL_DAY.end;
}
